import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { 
    Dialog, 
    DialogContent, 
    DialogHeader, 
    DialogTitle, 
    DialogTrigger,
    DialogFooter
} from '../components/ui/dialog';
import { customersAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    Plus, 
    Search, 
    User, 
    Phone, 
    Mail, 
    ChevronRight,
    Loader2,
    MapPin
} from 'lucide-react';

export const Customers = () => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        notes: ''
    });

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async (search = '') => {
        try {
            const response = await customersAPI.list(search);
            setCustomers(response.data);
        } catch (error) {
            console.error('Failed to fetch customers:', error);
            toast.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        // Debounced search
        const timeoutId = setTimeout(() => {
            fetchCustomers(query);
        }, 300);
        return () => clearTimeout(timeoutId);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            toast.error('Customer name is required');
            return;
        }

        setSaving(true);
        try {
            const response = await customersAPI.create({
                ...formData,
                addresses: []
            });
            toast.success('Customer created successfully!');
            setDialogOpen(false);
            setFormData({ name: '', phone: '', email: '', notes: '' });
            fetchCustomers();
            // Optionally navigate to new customer
            navigate(`/customers/${response.data.id}`);
        } catch (error) {
            console.error('Failed to create customer:', error);
            toast.error('Failed to create customer');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-20 w-full rounded-lg" />
                    ))}
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl lg:text-3xl font-bold uppercase tracking-tight text-slate-900">
                            Customers
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">{customers.length} total</p>
                    </div>
                    
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button 
                                className="h-12 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide shadow-sm"
                                data-testid="add-customer-btn"
                            >
                                <Plus className="h-5 w-5 mr-2" />
                                <span className="hidden sm:inline">Add</span>
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl uppercase tracking-tight">
                                    Add Customer
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Name *
                                    </Label>
                                    <Input
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Customer name"
                                        className="h-12 border-slate-300"
                                        data-testid="customer-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Phone
                                    </Label>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Phone number"
                                        className="h-12 border-slate-300"
                                        data-testid="customer-phone-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Email
                                    </Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Email address"
                                        className="h-12 border-slate-300"
                                        data-testid="customer-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="notes" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Notes
                                    </Label>
                                    <Input
                                        id="notes"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        placeholder="Access, parking, preferences..."
                                        className="h-12 border-slate-300"
                                        data-testid="customer-notes-input"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button
                                        type="submit"
                                        disabled={saving}
                                        className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase"
                                        data-testid="save-customer-btn"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Customer'
                                        )}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                        type="search"
                        placeholder="Search by name, phone or email..."
                        value={searchQuery}
                        onChange={handleSearch}
                        className="h-12 pl-10 border-slate-300"
                        data-testid="customer-search"
                    />
                </div>

                {/* Customer List */}
                {customers.length > 0 ? (
                    <div className="space-y-2">
                        {customers.map((customer) => (
                            <Link
                                key={customer.id}
                                to={`/customers/${customer.id}`}
                                data-testid={`customer-card-${customer.id}`}
                            >
                                <Card className="bg-white border-slate-200 shadow-sm hover:border-red-200 hover:shadow-md transition-all">
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <User className="h-6 w-6 text-slate-400" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-slate-900 truncate">
                                                        {customer.name}
                                                    </p>
                                                    <div className="flex items-center gap-3 text-sm text-slate-500">
                                                        {customer.phone && (
                                                            <span className="flex items-center gap-1">
                                                                <Phone className="h-3 w-3" />
                                                                {customer.phone}
                                                            </span>
                                                        )}
                                                        {customer.email && (
                                                            <span className="flex items-center gap-1 truncate">
                                                                <Mail className="h-3 w-3" />
                                                                {customer.email}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-white border-slate-200">
                        <CardContent className="p-8 text-center">
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <User className="h-8 w-8 text-slate-400" />
                            </div>
                            <p className="text-slate-600 font-medium">No customers found</p>
                            <p className="text-slate-500 text-sm mt-1 mb-4">
                                {searchQuery ? 'Try a different search term' : 'Add your first customer to get started'}
                            </p>
                            {!searchQuery && (
                                <Button
                                    onClick={() => setDialogOpen(true)}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                    data-testid="empty-add-customer-btn"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Customer
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};
